import { Activity, Building2, CheckCircle2, Languages, MapPinned, UsersRound } from "lucide-react";
import { Country, CountryId, countries, dashboardSeries } from "../data";
import MetricCard from "./MetricCard";
import { formatNumber } from "../lib/scenario";

type ProgramDashboardProps = {
  selected: Country;
  onSelect: (id: CountryId) => void;
};

function sparkPath(values: number[], width = 150, height = 44) {
  const max = Math.max(...values);
  const min = Math.min(...values);
  return values
    .map((value, index) => {
      const x = (index / Math.max(values.length - 1, 1)) * width;
      const y = height - ((value - min) / Math.max(max - min, 1)) * height;
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");
}

function Donut({ value, label }: { value: number; label: string }) {
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className="donut-wrap">
      <svg viewBox="0 0 112 112" className="donut">
        <circle cx="56" cy="56" r={radius} />
        <circle cx="56" cy="56" r={radius} strokeDasharray={`${circumference}`} strokeDashoffset={`${circumference - (value / 100) * circumference}`} />
      </svg>
      <div className="donut-label">
        <strong>{value.toFixed(1)}%</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

export default function ProgramDashboard({ selected, onSelect }: ProgramDashboardProps) {
  const totalCommunities = countries.reduce((sum, item) => sum + item.communities, 0);
  const totalPhrases = countries.reduce((sum, item) => sum + item.phraseTarget, 0);
  const validated = Math.round(totalPhrases * (selected.qaPassPct / 100));
  const selectedSeries = dashboardSeries.map((point, index) => ({
    ...point,
    selected: Math.round(point.validated * (0.42 + selected.adoptionPct / 130) * (0.92 + index * 0.018)),
  }));

  return (
    <section id="evidence" className="section band dashboard-band">
      <div className="section-heading">
        <div>
          <span className="section-index">01</span>
          <h2>Program Intelligence</h2>
        </div>
        <p>Country selection, validation evidence and implementation metrics update together from the same operating model.</p>
        <div className="live-status"><span /> Live model</div>
      </div>

      <div className="country-tabs" role="tablist" aria-label="Country dashboard selector">
        {countries.map((country) => (
          <button
            key={country.id}
            type="button"
            className={country.id === selected.id ? "active" : ""}
            onClick={() => onSelect(country.id)}
          >
            {country.name}
          </button>
        ))}
      </div>

      <div className="metric-grid dashboard-metrics">
        <MetricCard label="Communities engaged" value={formatNumber(totalCommunities)} detail={`Selected: ${selected.communities}`} icon={<UsersRound />} tone="green" />
        <MetricCard label="Language tracks" value={String(selected.languages.length + selected.readinessBriefs.length)} detail={`${selected.languages.length} full packs`} icon={<Languages />} tone="teal" />
        <MetricCard label="Phrase target" value={formatNumber(selected.phraseTarget)} detail={`${formatNumber(validated)} QA passed`} icon={<Activity />} tone="gold" />
        <MetricCard label="Service journeys" value={String(selected.serviceJourneys)} detail={`${selected.fieldSites} field sites`} icon={<MapPinned />} tone="green" />
        <MetricCard label="Policy engagement" value={`${selected.adoptionPct}%`} detail="Adoption probability" icon={<Building2 />} tone="clay" />
        <MetricCard label="QA pass rate" value={`${selected.qaPassPct.toFixed(1)}%`} detail="Grounding and refusal checks" icon={<CheckCircle2 />} tone="teal" />
      </div>

      <div className="dashboard-grid">
        <div className="viz-panel validation-panel">
          <div className="panel-title">
            <h3>Data Collection Progress</h3>
            <span>{selected.name}</span>
          </div>
          <Donut value={selected.qaPassPct} label="QA pass rate" />
          <ul className="legend-list">
            <li><i className="green-dot" />Validated <strong>{formatNumber(validated)}</strong></li>
            <li><i className="teal-dot" />In review <strong>{formatNumber(Math.round(selected.phraseTarget * 0.18))}</strong></li>
            <li><i className="gold-dot" />Collected <strong>{formatNumber(selected.phraseTarget)}</strong></li>
            <li><i className="clay-dot" />Major issues <strong>{formatNumber(Math.round(selected.phraseTarget * 0.011))}</strong></li>
          </ul>
        </div>

        <div className="viz-panel line-panel">
          <div className="panel-title">
            <h3>Validation Trajectory</h3>
            <span>M1-M18</span>
          </div>
          <svg viewBox="0 0 520 220" className="line-chart" aria-label="Validation trajectory chart">
            {[0, 1, 2, 3].map((item) => <line key={item} x1="42" x2="500" y1={42 + item * 42} y2={42 + item * 42} />)}
            <path d={sparkPath(selectedSeries.map((point) => point.collected), 440, 150)} transform="translate(45 34)" className="chart-line chart-line-gold" />
            <path d={sparkPath(selectedSeries.map((point) => point.selected), 440, 150)} transform="translate(45 34)" className="chart-line chart-line-teal" />
            {selectedSeries.map((point, index) => (
              <g key={point.month} transform={`translate(${45 + (index / (selectedSeries.length - 1)) * 440} 196)`}>
                <text>{point.month}</text>
              </g>
            ))}
          </svg>
        </div>

        <div className="viz-panel country-panel">
          <div className="panel-title">
            <h3>Country Context</h3>
            <span>{selected.role}</span>
          </div>
          <div className="context-grid">
            <span>Population</span><strong>{selected.populationM.toFixed(1)}M</strong>
            <span>Poverty exposure</span><strong>{selected.povertyPct.toFixed(1)}%</strong>
            <span>Adult literacy</span><strong>{selected.literacyPct.toFixed(1)}%</strong>
            <span>Internet use</span><strong>{selected.internetPct.toFixed(1)}%</strong>
          </div>
          <p>{selected.baselineNeed}</p>
        </div>

        <div className="viz-panel coverage-panel">
          <div className="panel-title">
            <h3>Coverage by Country</h3>
            <span>% target communities</span>
          </div>
          <div className="bar-list">
            {countries.map((country) => (
              <button key={country.id} type="button" onClick={() => onSelect(country.id)} className={country.id === selected.id ? "active" : ""}>
                <span>{country.name}</span>
                <i><b style={{ width: `${country.adoptionPct}%` }} /></i>
                <strong>{country.adoptionPct}%</strong>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
